/**
 * SchemaSelect — shared schema-picker UI used by both the Target tab's
 * single "Target Schema" dropdown (TargetTablePanel) and the Source tab's
 * per-row "Schema" dropdown (SourceTablesPanel).
 *
 * Offers the connection's real non-system schemas as a dropdown, with a
 * "Enter manually instead" fallback toggle for schemas not in the list (or
 * when the list failed to load / connection has no credentials yet).
 */

import { Loader2 } from "lucide-react";

const INPUT_CLASS =
  "w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-white placeholder-white/30 font-mono focus:outline-none focus:border-brand-500/50 focus:ring-1 focus:ring-brand-500/30";

interface SchemaSelectProps {
  id: string;
  value: string;
  schemas: string[];
  loading: boolean;
  error: string | null;
  manualEntry: boolean;
  onManualEntryToggle: () => void;
  onChange: (value: string) => void;
}

export function SchemaSelect({
  id,
  value,
  schemas,
  loading,
  error,
  manualEntry,
  onManualEntryToggle,
  onChange,
}: SchemaSelectProps) {
  return (
    <div className="space-y-1">
      {manualEntry ? (
        <input
          id={id}
          type="text"
          className={INPUT_CLASS}
          placeholder="schema"
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
      ) : (
        <select
          id={id}
          className="w-full bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-sm text-white"
          value={value}
          onChange={(e) => onChange(e.target.value)}
        >
          {value === "" && <option value="">— Select a schema —</option>}
          {schemas.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      )}
      <div className="flex items-center gap-2">
        {loading && (
          <Loader2
            size={12}
            className="animate-spin text-white/40"
            aria-hidden="true"
          />
        )}
        {schemas.length > 0 && (
          <button
            type="button"
            onClick={onManualEntryToggle}
            className="text-[10px] text-white/40 hover:text-white/60 underline"
          >
            {manualEntry
              ? "Choose from list instead"
              : "Enter schema manually instead"}
          </button>
        )}
      </div>
      {error !== null && (
        <p className="text-red-400 text-xs" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
